import type { AgentSchema, AgentSchemaTool } from '@agentskit/core/agent-schema'

export interface ScaffoldFile {
  path: string
  content: string
}

function snakeToCamel(name: string): string {
  return name.replace(/[-_](\w)/g, (_, c: string) => c.toUpperCase())
}

function toolStub(tool: AgentSchemaTool): string {
  const fnName = snakeToCamel(tool.name)
  const schema = tool.schema ? JSON.stringify(tool.schema, null, 2) : '{ type: "object", properties: {} }'
  return `import { defineTool } from '@agentskit/core'

/**
 * ${tool.description ?? tool.name}
 * ${tool.implementation ? `\n * Implementation hint: ${tool.implementation}` : ''}
 */
export const ${fnName} = defineTool({
  name: '${tool.name}',
  description: ${JSON.stringify(tool.description ?? '')},
  schema: ${schema} as const,
  ${tool.requiresConfirmation ? 'requiresConfirmation: true,' : ''}
  async execute(args) {
    // TODO: implement ${tool.name}
    return { args }
  },
})
`
}

function indexTs(schema: AgentSchema): string {
  const toolImports = (schema.tools ?? [])
    .map(t => `import { ${snakeToCamel(t.name)} } from './tools/${t.name}'`)
    .join('\n')
  const toolList = (schema.tools ?? []).map(t => snakeToCamel(t.name)).join(', ')
  return `import { createRuntime } from '@agentskit/runtime'
${toolImports}

export const agent = {
  name: '${schema.name}',
  systemPrompt: ${JSON.stringify(schema.systemPrompt ?? '')},
  tools: [${toolList}],
}

export function createAgent(adapter: Parameters<typeof createRuntime>[0]['adapter']) {
  return createRuntime({
    adapter,
    systemPrompt: agent.systemPrompt,
    tools: agent.tools,
  })
}
`
}

function readme(schema: AgentSchema): string {
  const lines: string[] = []
  lines.push(`# ${schema.name}`)
  lines.push('')
  if (schema.description) lines.push(schema.description, '')
  lines.push(`## Model`)
  lines.push('')
  lines.push(`- Provider: \`${schema.model.provider}\``)
  if (schema.model.model) lines.push(`- Model: \`${schema.model.model}\``)
  lines.push('')
  if (schema.tools && schema.tools.length > 0) {
    lines.push('## Tools')
    lines.push('')
    for (const t of schema.tools) {
      lines.push(`- \`${t.name}\` — ${t.description ?? ''}`)
    }
    lines.push('')
  }
  if (schema.skills && schema.skills.length > 0) {
    lines.push('## Skills')
    lines.push('')
    for (const s of schema.skills) lines.push(`- ${s}`)
    lines.push('')
  }
  lines.push('## Generated by')
  lines.push('')
  lines.push('`npx agentskit ai "<description>"`')
  lines.push('')
  return lines.join('\n')
}

/**
 * Render an `AgentSchema` into the set of files a project needs:
 * `agent.json`, `agent.ts` (runtime wrapper), tool stubs, and a
 * README. Returns the files without touching disk — callers decide
 * where to write.
 */
export function scaffoldAgent(schema: AgentSchema): ScaffoldFile[] {
  const files: ScaffoldFile[] = [
    { path: 'agent.json', content: JSON.stringify(schema, null, 2) + '\n' },
    { path: 'agent.ts', content: indexTs(schema) },
    { path: 'README.md', content: readme(schema) },
  ]
  for (const tool of schema.tools ?? []) {
    files.push({ path: `tools/${tool.name}.ts`, content: toolStub(tool) })
  }
  return files
}

export async function writeScaffold(
  files: ScaffoldFile[],
  outDir: string,
  opts: { overwrite?: boolean } = {},
): Promise<string[]> {
  const { writeFile, mkdir, access } = await import('node:fs/promises')
  const { dirname, join } = await import('node:path')
  const written: string[] = []
  for (const f of files) {
    const full = join(outDir, f.path)
    await mkdir(dirname(full), { recursive: true })
    if (!opts.overwrite) {
      try {
        await access(full)
        continue // skip existing
      } catch {
        // file does not exist — proceed
      }
    }
    await writeFile(full, f.content, 'utf8')
    written.push(f.path)
  }
  return written
}
